import { lazy, Suspense, type ComponentType } from "react"
import { Link, Navigate, Outlet, Route, Routes } from "react-router-dom"

import { MarketingScroll } from "@/components/routing/marketing-scroll"
import { HomeRoute } from "@/components/routing/home-route"

function lazyRoute(load: () => Promise<Record<string, unknown>>, exportName = "default") {
  return lazy(() =>
    load().then((mod) => ({ default: mod[exportName] as ComponentType }))
  )
}

const PlatformPage = lazyRoute(() => import("@/components/pages/platform-page"), "PlatformPage")
const TeamPage = lazyRoute(() => import("@/components/pages/team/team-page"), "TeamPage")
const PlayersPage = lazyRoute(() => import("@/components/pages/roster/players-page"), "PlayersPage")
const PlayerCvPage = lazyRoute(() => import("@/components/pages/roster/player-cv-page"), "PlayerCvPage")
const PrivacyPolicyPage = lazyRoute(
  () => import("@/components/pages/landing/privacy-policy-page"),
  "PrivacyPolicyPage"
)
const TermsOfUsePage = lazyRoute(
  () => import("@/components/pages/landing/terms-of-use-page"),
  "TermsOfUsePage"
)

const ContactPage = lazyRoute(() => import("@/app/contact/page.web"))
const LoginPage = lazyRoute(() => import("@/app/login/page.web"))
const ForgotPasswordPage = lazyRoute(() => import("@/app/forgot-password/page.web"))
const SetPasswordPage = lazyRoute(() => import("@/app/set-password/page.web"))
const ResetPasswordPage = lazyRoute(() => import("@/app/reset-password/page.web"))
const ProfileRedirectPage = lazyRoute(() => import("@/app/profile/page.web"))

const DashboardPage = lazyRoute(() => import("@/app/dashboard/page.web"))
const DashboardAdvisorPage = lazyRoute(() => import("@/app/dashboard/advisor/page.web"))
const DashboardInvestmentsPage = lazyRoute(() => import("@/app/dashboard/investments/page.web"))
const DashboardProspectsPage = lazyRoute(() => import("@/app/dashboard/prospects/page.web"))
const DashboardPlayersPage = lazyRoute(() => import("@/app/dashboard/players/page.web"))
const DashboardProfilePage = lazyRoute(() => import("@/app/dashboard/profile/page.web"))

const DashboardAdminPage = lazyRoute(() => import("@/app/dashboard/admin/page.web"))
const DashboardAdminUsersPage = lazyRoute(() => import("@/app/dashboard/admin/users/page.web"))
const DashboardAdminPlayersPage = lazyRoute(() => import("@/app/dashboard/admin/players/page.web"))
const DashboardAdminRosterPage = lazyRoute(() => import("@/app/dashboard/admin/roster/page.web"))
const DashboardAdminRosterNewPage = lazyRoute(() => import("@/app/dashboard/admin/roster/new/page.web"))
const DashboardAdminRosterEditPage = lazyRoute(() => import("@/app/dashboard/admin/roster/[id]/page.web"))
const DashboardAdminRosterPreviewPage = lazyRoute(
  () => import("@/app/dashboard/admin/roster/[id]/preview/page.web")
)
const DashboardAdminRequestsPage = lazyRoute(() => import("@/app/dashboard/admin/requests/page.web"))
const DashboardAdminExpertsPage = lazyRoute(() => import("@/app/dashboard/admin/experts/page.web"))
const DashboardAdminExpertsNewPage = lazyRoute(() => import("@/app/dashboard/admin/experts/new/page.web"))
const DashboardAdminExpertsEditPage = lazyRoute(() => import("@/app/dashboard/admin/experts/[id]/page.web"))
const DashboardAdminExpertsPreviewPage = lazyRoute(
  () => import("@/app/dashboard/admin/experts/[id]/preview/page.web")
)
const DashboardAdminFansPage = lazyRoute(() => import("@/app/dashboard/admin/fans/page.web"))
const DashboardAdminFansUsersPage = lazyRoute(() => import("@/app/dashboard/admin/fans/users/page.web"))
const DashboardAdminFansSeasonsPage = lazyRoute(() => import("@/app/dashboard/admin/fans/seasons/page.web"))
const DashboardAdminFansRewardsPage = lazyRoute(() => import("@/app/dashboard/admin/fans/rewards/page.web"))

const PlayerChatPage = lazyRoute(() => import("@/app/dashboard/player/chat/page.web"))
const PlayerComparePage = lazyRoute(() => import("@/app/dashboard/player/compare/page.web"))
const PlayerContractsPage = lazyRoute(() => import("@/app/dashboard/player/contracts/page.web"))
const PlayerDocumentsPage = lazyRoute(() => import("@/app/dashboard/player/documents/page.web"))
const PlayerFinancialPage = lazyRoute(() => import("@/app/dashboard/player/financial/page.web"))
const PlayerFitnessPage = lazyRoute(() => import("@/app/dashboard/player/fitness/page.web"))
const PlayerMentalPage = lazyRoute(() => import("@/app/dashboard/player/mental/page.web"))
const PlayerNewsPage = lazyRoute(() => import("@/app/dashboard/player/news/page.web"))
const PlayerOverviewPage = lazyRoute(() => import("@/app/dashboard/player/overview/page.web"))
const PlayerSocialPage = lazyRoute(() => import("@/app/dashboard/player/social/page.web"))
const PlayerStatisticsPage = lazyRoute(() => import("@/app/dashboard/player/statistics/page.web"))
const PlayerStatisticsCompetitionPage = lazyRoute(
  () => import("@/app/dashboard/player/statistics/competition/page.web")
)
const PlayerExpertsPage = lazyRoute(() => import("@/app/dashboard/player/experts/page.web"))
const PlayerExpertCvPage = lazyRoute(() => import("@/app/dashboard/player/experts/[slug]/page.web"))

const FanLoginPage = lazyRoute(() => import("@/app/fan/page.web"))
const FanLeaderboardPage = lazyRoute(() => import("@/app/fan/(app)/leaderboard/page.web"))
const FanPlayerPage = lazyRoute(() => import("@/app/fan/(app)/player/page.web"))
const FanPlayersPage = lazyRoute(() => import("@/app/fan/(app)/players/page.web"))
const FanPredictionsPage = lazyRoute(() => import("@/app/fan/(app)/predictions/page.web"))
const FanProfilePage = lazyRoute(() => import("@/app/fan/(app)/profile/page.web"))
const FanRewardsPage = lazyRoute(() => import("@/app/fan/(app)/rewards/page.web"))

const DashboardLayout = lazyRoute(() => import("@/app/dashboard/layout.shared"))
const PlayerDashboardLayout = lazyRoute(() => import("@/app/dashboard/player/layout.shared"))
const FanLayout = lazyRoute(() => import("@/app/fan/(app)/layout.shared"))

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">Page not found</h1>
        <p className="text-sm text-muted-foreground">The page you tried to open doesn&apos;t exist.</p>
        <div className="pt-4">
          <Link to="/dashboard" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
            Back to SigmAI
          </Link>
        </div>
      </div>
    </div>
  )
}

export function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <MarketingScroll />
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/platform" element={<PlatformPage />} />
        <Route path="/app" element={<Navigate to="/platform" replace />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/our-team" element={<Navigate to="/team" replace />} />
        <Route path="/players" element={<PlayersPage />} />
        <Route path="/players/s/:shareToken" element={<PlayerCvPage />} />
        <Route path="/players/:slug" element={<PlayerCvPage />} />
        <Route path="/about" element={<Navigate to="/" replace />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/profile" element={<ProfileRedirectPage />} />

        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="profile" element={<DashboardProfilePage />} />
          <Route path="advisor" element={<DashboardAdvisorPage />} />
          <Route path="investments" element={<DashboardInvestmentsPage />} />
          <Route path="prospects" element={<DashboardProspectsPage />} />
          <Route path="players" element={<DashboardPlayersPage />} />

          <Route path="admin" element={<DashboardAdminPage />} />
          <Route path="admin/users" element={<DashboardAdminUsersPage />} />
          <Route path="admin/players" element={<DashboardAdminPlayersPage />} />
          <Route path="admin/roster" element={<DashboardAdminRosterPage />} />
          <Route path="admin/roster/new" element={<DashboardAdminRosterNewPage />} />
          <Route path="admin/roster/:id/preview" element={<DashboardAdminRosterPreviewPage />} />
          <Route path="admin/roster/:id" element={<DashboardAdminRosterEditPage />} />
          <Route path="admin/requests" element={<DashboardAdminRequestsPage />} />
          <Route path="admin/experts" element={<DashboardAdminExpertsPage />} />
          <Route path="admin/experts/new" element={<DashboardAdminExpertsNewPage />} />
          <Route path="admin/experts/:id/preview" element={<DashboardAdminExpertsPreviewPage />} />
          <Route path="admin/experts/:id" element={<DashboardAdminExpertsEditPage />} />
          <Route path="admin/fans" element={<DashboardAdminFansPage />} />
          <Route path="admin/fans/users" element={<DashboardAdminFansUsersPage />} />
          <Route path="admin/fans/seasons" element={<DashboardAdminFansSeasonsPage />} />
          <Route path="admin/fans/rewards" element={<DashboardAdminFansRewardsPage />} />

          <Route path="player" element={<PlayerDashboardLayout />}>
            <Route path="chat" element={<PlayerChatPage />} />
            <Route path="compare" element={<PlayerComparePage />} />
            <Route path="contracts" element={<PlayerContractsPage />} />
            <Route path="documents" element={<PlayerDocumentsPage />} />
            <Route path="financial" element={<PlayerFinancialPage />} />
            <Route path="fitness" element={<PlayerFitnessPage />} />
            <Route path="mental" element={<PlayerMentalPage />} />
            <Route path="news" element={<PlayerNewsPage />} />
            <Route path="overview" element={<PlayerOverviewPage />} />
            <Route path="social" element={<PlayerSocialPage />} />
            <Route path="statistics/competition" element={<PlayerStatisticsCompetitionPage />} />
            <Route path="statistics" element={<PlayerStatisticsPage />} />
            <Route path="experts/:slug" element={<PlayerExpertCvPage />} />
            <Route path="experts" element={<PlayerExpertsPage />} />
          </Route>
        </Route>

        <Route path="fan" element={<Outlet />}>
          <Route index element={<FanLoginPage />} />
          <Route element={<FanLayout />}>
            <Route path="leaderboard" element={<FanLeaderboardPage />} />
            <Route path="player" element={<FanPlayerPage />} />
            <Route path="players" element={<FanPlayersPage />} />
            <Route path="predictions" element={<FanPredictionsPage />} />
            <Route path="profile" element={<FanProfilePage />} />
            <Route path="rewards" element={<FanRewardsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}
